import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useTranslation } from "react-i18next";

import {
  GetDemoRequestsCall,
  UpdateDemoRequestStatusCall,
  DeleteDemoRequestCall,
} from "../../redux/api/demoRequest.api";

const TIME_LABELS = {
  morning: "Morning (9am–12pm)",
  midday: "Midday (12pm–3pm)",
  afternoon: "Afternoon (3pm–6pm)",
  flexible: "Flexible",
};

const PILOT_LABELS = {
  yes: "Yes, I am interested",
  maybe: "Maybe later",
  not_now: "Not right now",
};

const DEFAULT_STATUS_KEYS = [
  "new_request",
  "contacted",
  "scheduled",
  "completed",
  "no_show",
  "rejected",
  "client",
];

const STATUS_LABELS_EN = {
  new_request: "New Request",
  contacted: "Contacted",
  scheduled: "Scheduled",
  completed: "Completed",
  no_show: "No Show",
  rejected: "Rejected",
  client: "Client",
};

const cellSx = { py: 1.1, px: 1.25, fontSize: "0.78rem", verticalAlign: "top" };

function formatDates(demoDates) {
  if (!Array.isArray(demoDates) || demoDates.length === 0) return "—";
  return demoDates.join(", ");
}

function formatDemoDatesPretty(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "—";
  return arr
    .map((ymd) => {
      try {
        const [y, m, d] = String(ymd).split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return ymd;
      }
    })
    .join("; ");
}

function labelTime(v) {
  if (v == null || v === "") return "—";
  return TIME_LABELS[String(v)] || String(v);
}

function labelPilot(v) {
  if (v == null || v === "") return "—";
  return PILOT_LABELS[String(v)] || String(v);
}

function statusChipColor(key) {
  switch (key) {
    case "new_request":
      return "info";
    case "contacted":
      return "primary";
    case "scheduled":
      return "secondary";
    case "completed":
      return "success";
    case "no_show":
      return "warning";
    case "rejected":
      return "error";
    case "client":
      return "default";
    default:
      return "default";
  }
}

function flattenForDisplay(obj, prefix = "") {
  if (obj == null) return [];
  if (typeof obj !== "object") {
    return [{ key: prefix || "value", value: String(obj) }];
  }
  if (Array.isArray(obj)) {
    return [{ key: prefix || "items", value: JSON.stringify(obj) }];
  }
  const out = [];
  Object.keys(obj).forEach((k) => {
    const path = prefix ? `${prefix}.${k}` : k;
    const val = obj[k];
    if (val == null) {
      out.push({ key: path, value: "—" });
    } else if (typeof val === "object" && !Array.isArray(val)) {
      out.push(...flattenForDisplay(val, path));
    } else if (Array.isArray(val)) {
      out.push({ key: path, value: JSON.stringify(val) });
    } else {
      out.push({ key: path, value: String(val) });
    }
  });
  return out;
}

export default function DemoRequestsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [statusKeys, setStatusKeys] = useState(DEFAULT_STATUS_KEYS);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    color: "success",
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { rows: data, statuses } = await GetDemoRequestsCall();
    setRows(Array.isArray(data) ? data : []);
    if (Array.isArray(statuses) && statuses.length > 0) {
      setStatusKeys(statuses);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const paged = useMemo(
    () =>
      rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const showToast = (message, color = "success") => {
    setSnackbar({ show: true, message, color });
  };

  const handleSetStatus = async (status) => {
    if (!menuRow) return;
    closeMenu();
    const res = await UpdateDemoRequestStatusCall(menuRow.id, status);
    if (res?.success && res?.data) {
      setRows((prev) =>
        prev.map((r) => (r.id === menuRow.id ? { ...r, ...res.data } : r))
      );
      showToast(t("demoRequests.statusUpdated", "Status updated."));
    } else {
      showToast(
        t("demoRequests.statusError", "Could not update status."),
        "error"
      );
    }
  };

  const openDetails = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    closeMenu();
  };

  const confirmDelete = (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
    closeMenu();
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    const res = await DeleteDemoRequestCall(id);
    setDeleteOpen(false);
    setDeleteTarget(null);
    if (res?.success) {
      setRows((prev) => prev.filter((r) => r.id !== id));
      showToast(t("demoRequests.deleted", "Request deleted."));
    } else {
      showToast(
        t("demoRequests.deleteError", "Could not delete."),
        "error"
      );
    }
  };

  const statusLabel = (key) =>
    t(`demoRequests.status.${key}`, STATUS_LABELS_EN[key] || key);

  const renderDetailContent = (row) => {
    if (!row) return null;
    const u = row.user;
    const uniSnap = row.university;
    const submittedAt = row.created_at
      ? new Date(row.created_at).toISOString()
      : "—";
    const timePref = labelTime(row.time_preference);
    const pilot = labelPilot(row.pilot);
    const demoDatesPretty = formatDemoDatesPretty(row.demo_dates);

    const requestKv = [
      ["Request ID", row.id],
      ["Submitted (UTC)", submittedAt],
      ["Updated (UTC)", row.updated_at ? new Date(row.updated_at).toISOString() : "—"],
      ["CRM status", statusLabel(row.status || "new_request")],
      ["Work email", row.work_email],
      ["Phone (form)", row.phone || "—"],
      ["Academic role", row.academic_role],
      ["Teaching area", row.teaching_area],
      ["Class size", row.class_size || "—"],
      ["Main interest", row.main_interest || "—"],
      ["Main goal", row.main_goal || "—"],
      ["Preferred dates (raw)", formatDates(row.demo_dates)],
      ["Preferred dates (formatted)", demoDatesPretty],
      ["Time preference", timePref],
      ["Pilot interest", pilot],
      ["Account role (at submit)", row.account_role || "—"],
      ["University (snapshot name)", uniSnap?.name || "—"],
    ];

    return (
      <Box>
        <Typography variant="subtitle2" color="text" gutterBottom>
          {t("demoRequests.detailRequestSection", "Demo request (same fields as admin email)")}
        </Typography>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", mb: 2 }}>
          <tbody>
            {requestKv.map(([k, v]) => (
              <tr key={k}>
                <Box
                  component="td"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    p: 1,
                    width: "38%",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    verticalAlign: "top",
                  }}
                >
                  {k}
                </Box>
                <Box
                  component="td"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    p: 1,
                    fontSize: "0.8rem",
                    wordBreak: "break-word",
                    verticalAlign: "top",
                  }}
                >
                  {v}
                </Box>
              </tr>
            ))}
          </tbody>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text" gutterBottom>
          {t("demoRequests.detailAccountSection", "Microlab user profile")}
        </Typography>
        {u ? (
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {flattenForDisplay(u).map(({ key, value }) => (
                <tr key={key}>
                  <Box
                    component="td"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      p: 0.75,
                      width: "38%",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      verticalAlign: "top",
                    }}
                  >
                    {key}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      p: 0.75,
                      fontSize: "0.75rem",
                      wordBreak: "break-word",
                      verticalAlign: "top",
                    }}
                  >
                    {value}
                  </Box>
                </tr>
              ))}
            </tbody>
          </Box>
        ) : (
          <Typography variant="body2" color="text">
            {t("demoRequests.noUser", "No linked user.")}
          </Typography>
        )}

        <Typography variant="caption" display="block" sx={{ mt: 2, opacity: 0.8 }}>
          {t(
            "demoRequests.detailFootnote",
            "User object includes linked university (universityInfo) when present."
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h5" fontWeight="medium">
            {t("demoRequests.title", "Demo requests")}
          </MDTypography>
          <MDTypography variant="body2" color="text" mt={0.5}>
            {t(
              "demoRequests.subtitle",
              "Submissions from the mobile app “Request a demo” flow. Set status, open details, or delete."
            )}
          </MDTypography>
        </MDBox>
        <Card>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </MDBox>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: "calc(100vh - 240px)" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colSubmitted", "Submitted")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colStatus", "Status")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colWorkEmail", "Work email")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colAccount", "Account")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colRole", "Academic role")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colArea", "Teaching area")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colDates", "Preferred dates")}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {t("demoRequests.colTime", "Time")}
                      </TableCell>
                      <TableCell sx={cellSx} align="right" width={56}>
                        {" "}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          {t("demoRequests.empty", "No demo requests yet.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((row) => {
                        const u = row.user;
                        const accountLabel = u
                          ? `${u.first_name || ""} ${u.last_name || ""} (${u.email || ""})`.trim()
                          : "—";
                        const st = row.status || "new_request";
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell sx={cellSx}>
                              {row.created_at
                                ? moment(row.created_at).format("YYYY-MM-DD HH:mm")
                                : "—"}
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <Chip
                                style={{ color: "white", padding: 2 }}
                                size="small"
                                label={statusLabel(st)}
                                color={statusChipColor(st)}
                                variant={st === "client" ? "outlined" : "filled"}
                              />
                            </TableCell>
                            <TableCell sx={cellSx}>{row.work_email || "—"}</TableCell>
                            <TableCell
                              sx={{
                                ...cellSx,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                maxWidth: 200,
                              }}
                            >
                              {accountLabel}
                            </TableCell>
                            <TableCell sx={cellSx}>{row.academic_role || "—"}</TableCell>
                            <TableCell sx={cellSx}>{row.teaching_area || "—"}</TableCell>
                            <TableCell sx={{ ...cellSx, whiteSpace: "normal" }}>
                              {formatDates(row.demo_dates)}
                            </TableCell>
                            <TableCell sx={cellSx}>{labelTime(row.time_preference)}</TableCell>
                            <TableCell align="right" sx={{ ...cellSx, width: 48 }}>
                              <IconButton
                                size="small"
                                aria-label="actions"
                                onClick={(e) => openMenu(e, row)}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Card>
      </MDBox>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <ListSubheader sx={{ lineHeight: 2 }}>
          {t("demoRequests.setStatus", "Set status")}
        </ListSubheader>
        {statusKeys.map((key) => (
          <MenuItem
            key={key}
            dense
            selected={(menuRow?.status || "new_request") === key}
            onClick={() => handleSetStatus(key)}
          >
            <ListItemText primary={statusLabel(key)} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => menuRow && openDetails(menuRow)}
        >
          {t("demoRequests.moreInfo", "More info")}
        </MenuItem>
        <MenuItem
          onClick={() => menuRow && confirmDelete(menuRow)}
          sx={{ color: "error.main" }}
        >
          {t("demoRequests.delete", "Delete")}
        </MenuItem>
      </Menu>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {t("demoRequests.detailTitle", "Request & account details")}
        </DialogTitle>
        <DialogContent dividers>
          {renderDetailContent(detailRow)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>
            {t("demoRequests.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>
          {t("demoRequests.deleteTitle", "Delete demo request?")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              "demoRequests.deleteBody",
              "This removes the row permanently. It cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>
            {t("demoRequests.cancel", "Cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={doDelete}>
            {t("demoRequests.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snackbar.color === "error" ? "error" : "success"}
        icon={snackbar.color === "error" ? "error" : "check_circle"}
        title={snackbar.color === "error" ? "Error" : "OK"}
        content={snackbar.message}
        dateTime=" "
        open={snackbar.show}
        close={() => setSnackbar((s) => ({ ...s, show: false }))}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
      />

      <Footer />
    </DashboardLayout>
  );
}
